/**
 * barclose.ts — "has a bar CLOSED since the last poll?", as a pure function.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * `pollLive()` repaints the forming candle every 5s but never re-asked the
 * detectors for anything, so a session left open across a bar close drew NEW
 * candles against OLD swing/major/range/FVG/liquidity/break geometry — with
 * nothing on screen saying the two were out of sync. Andrei reported it as a
 * dimmed `ML?` at ~1400 on `xyz:SNDK 1D` while price had already traded through
 * it down to 1228.
 *
 * Measured cost of not asking (replaying each series prefix-by-prefix through
 * the real `overlays.swings`, last 60 CLOSED bars, hyperliquid cache):
 *
 *   | series       | pivot set moved | major set moved |
 *   |--------------|-----------------|-----------------|
 *   | xyz:SNDK 1D  | 13/60 = 21.7%   | 7/60 = 11.7%    |
 *   | BTC 1D       |  8/60 = 13.3%   | 3/60 =  5.0%    |
 *   | BTC 15m      | 19/60 = 31.7%   | 7/60 = 11.7%    |
 *   | ETH 1h       | 18/60 = 30.0%   | 8/60 = 13.3%    |
 *
 * So intraday, roughly one close in three invalidates the drawn pivots.
 *
 * WHY A SEPARATE MODULE AND NOT AN `if` INSIDE pollLive
 * ----------------------------------------------------
 * Gate 5 of `scripts/verify.sh` (vitest) exists because a state bug that lived
 * inside `main.ts` was structurally unreachable by pytest and shipped green
 * through the other four gates. A rollover comparison buried in `pollLive`
 * cannot be tested; the same comparison here can be, including the cases that
 * actually bite — a duplicated poll, a missed poll, and a response that arrives
 * after the user switched asset.
 *
 * NO THRESHOLD LIVES HERE. "A bar closed" is a fact, not a parameter.
 */

/** The subset of `/api/live`'s payload this decision needs. */
export type RolloverPoll = {
  asset: string;
  tf: string;
  forming: { time: number } | null;
  closed_bar: { time: number } | null;
};

/** What the caller remembers between polls. */
export type RolloverState = {
  /** `forming.time` of the last poll applied for this asset/tf, or null. */
  formingTime: number | null;
  /** The asset/tf that `formingTime` belongs to — `null` before the first poll. */
  key: string | null;
};

export type RolloverDecision = {
  /** True iff a bar closed between the previous poll and this one. */
  rolledOver: boolean;
  /** The state to remember for the next poll. */
  next: RolloverState;
  /** Why — surfaced in the UI, so a recompute is never silent. */
  reason:
    | 'first-poll'
    | 'series-changed'
    | 'steady'
    | 'rollover'
    | 'stale-response'
    | 'no-forming-bar';
};

export const seriesKey = (asset: string, tf: string): string => `${asset}\u0000${tf}`;

export const initialRolloverState = (): RolloverState => ({ formingTime: null, key: null });

/**
 * Did a bar close between `prev` and `poll`?
 *
 * The predicate is `forming.time` INCREASING for the SAME series. That choice is
 * deliberate over the two obvious alternatives:
 *
 *   * counting polls or incrementing a counter — a duplicated or dropped poll
 *     then skews the count permanently (the same reasoning as `closedBarCount`
 *     being derived by position rather than incremented);
 *   * watching `closed_bar.time` — the server returns a `closed_bar` on EVERY
 *     poll in steady state, so it would fire continuously.
 *
 * `expectedKey` is the series the caller is CURRENTLY displaying. A response for
 * a different series is dropped (`stale-response`) without touching the state,
 * mirroring the guard `pollLive` already applies to bars: drawing BTC's pivots on
 * an ETH chart is worse than drawing stale ones.
 *
 * A forming time that moves BACKWARD is not a rollover (it is a stale or
 * out-of-order response); it is remembered so the next forward move is detected
 * from the truth rather than from a future value.
 */
export function detectRollover(
  prev: RolloverState,
  poll: RolloverPoll,
  expectedKey: string,
): RolloverDecision {
  const key = seriesKey(poll.asset, poll.tf);
  if (key !== expectedKey) {
    return { rolledOver: false, next: prev, reason: 'stale-response' };
  }
  const t = poll.forming?.time ?? null;
  if (t === null) {
    return { rolledOver: false, next: prev, reason: 'no-forming-bar' };
  }
  if (prev.key !== key) {
    // First poll for this series (boot, or an asset/tf switch). `loadBars()`
    // already drew overlays for the bars it just fetched, so this is NOT a
    // rollover — recomputing here would double every switch.
    return { rolledOver: false, next: { formingTime: t, key }, reason: prev.key === null ? 'first-poll' : 'series-changed' };
  }
  if (prev.formingTime !== null && t > prev.formingTime) {
    return { rolledOver: true, next: { formingTime: t, key }, reason: 'rollover' };
  }
  return { rolledOver: false, next: { formingTime: t, key }, reason: 'steady' };
}
